import { absoluteUrl } from '@/lib/seo/canonical-url';
import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';

/** 1200×630 brand card used when a page has no banner or product image to share. */
export const DEFAULT_OG_IMAGE_PATH = '/og-default.png';
export const DEFAULT_OG_IMAGE_WIDTH = 1200;
export const DEFAULT_OG_IMAGE_HEIGHT = 630;

export function defaultOgImageUrl(): string {
  return absoluteUrl(DEFAULT_OG_IMAGE_PATH);
}

export type SeoOgImage = {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
};

function toAbsoluteImageUrl(url: string): string {
  const trimmed = rewriteStaticAssetUrl(url.trim());
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return absoluteUrl(trimmed);
}

export function isUsableImageUrl(url: unknown): url is string {
  return typeof url === 'string' && url.trim().length > 0;
}

/**
 * Maps CMS / product image objects to next-seo Open Graph images.
 * Always returns at least the brand fallback so we never emit empty og:image.
 */
export function resolveOgImages(
  images: unknown[] | null | undefined,
  fallbackAlt = 'Qualidoc',
): SeoOgImage[] {
  const mapped: SeoOgImage[] = [];

  for (const item of images ?? []) {
    if (!item || typeof item !== 'object') continue;
    const record = item as {
      original?: string;
      url?: string;
      title?: string;
      alt?: string;
      image?: { original?: string };
    };
    const raw = record.original ?? record.url ?? record.image?.original;
    if (!isUsableImageUrl(raw)) continue;
    const url = toAbsoluteImageUrl(raw);
    if (!url) continue;
    mapped.push({
      url,
      alt: record.title ?? record.alt ?? fallbackAlt,
    });
  }

  if (mapped.length > 0) return mapped;

  return [
    {
      url: defaultOgImageUrl(),
      alt: fallbackAlt,
      width: DEFAULT_OG_IMAGE_WIDTH,
      height: DEFAULT_OG_IMAGE_HEIGHT,
    },
  ];
}
